import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api.js";
import {
  AttributionBadge, Badge, Button, EmptyState, Field, LoadingRows, Notice,
  SearchCombobox, SelectField, cn,
} from "./ui.jsx";

const CATEGORY_OPTIONS = [
  ["all", "Everything"],
  ["memory", "Memory"],
  ["email", "Email"],
  ["call", "Calls"],
  ["meeting", "Meetings"],
  ["enrichment", "Enrichment"],
  ["relationship", "Relationships"],
  ["lead", "Lead stages"],
  ["task", "Tasks"],
  ["communication", "Other"],
];

const CATEGORY_ICONS = {
  memory: "◇",
  email: "@",
  call: "⌕",
  meeting: "◫",
  enrichment: "↯",
  relationship: "↔",
  lead: "↗",
  task: "✓",
  communication: "·",
};

const CATEGORY_TINTS = {
  memory: "bg-primary/10 text-primary-600",
  email: "bg-blue-500/12 text-blue-700",
  call: "bg-teal-500/12 text-teal-700",
  meeting: "bg-violet-500/12 text-violet-700",
  enrichment: "bg-amber-500/15 text-amber-700",
  relationship: "bg-pink-500/12 text-pink-700",
  lead: "bg-green-500/12 text-green-700",
  task: "bg-green-500/12 text-green-700",
  communication: "bg-muted text-muted-foreground",
};

function formatDate(value) {
  if (!value) return "Unknown date";
  return new Intl.DateTimeFormat("en", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function dateBoundary(value, endOfDay = false) {
  if (!value) return "";
  const date = new Date(`${value}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}`);
  return date.toISOString();
}

function statusTone(status) {
  if (["active", "current", "sent", "succeeded", "verified", "catalog_hit"].includes(status)) return "succeeded";
  if (["failed", "outcome_unknown", "conflicting"].includes(status)) return "failed";
  if (["running", "pending", "pending_approval", "approved", "sending", "unknown"].includes(status)) return "running";
  if (["cancelled", "stale", "former", "superseded", "skipped"].includes(status)) return "stale";
  return undefined;
}

function RelatedEntity({ value, currentId, onOpen, suffix }) {
  if (!value || value.id === currentId) return null;
  if (!onOpen) return <Badge>{value.name || suffix}</Badge>;
  return <button type="button" className="text-[11px] font-medium text-primary hover:text-primary-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" onClick={() => onOpen(value.id)}>{value.name || suffix} →</button>;
}

function FilterChip({ active, children, ...props }) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex h-7 shrink-0 cursor-pointer items-center rounded-full px-3 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        active ? "bg-primary text-white shadow-[0_1px_2px_oklch(0_0_0_/_0.08)]" : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground",
      )}
      aria-pressed={active}
      {...props}
    >{children}</button>
  );
}

function TimelineEvent({ event, subjectId, onOpenCompany, onOpenPerson, compact = false }) {
  return (
    <article className={cn("relative grid gap-2 border-b border-neutral-100 py-5 pl-12 last:border-b-0", compact ? "sm:pl-14" : "sm:grid-cols-[132px_minmax(0,1fr)] sm:pl-14")}>
      <span className={cn("absolute left-[17px] top-5 z-10 grid size-7 place-items-center rounded-full text-[11px] font-semibold", CATEGORY_TINTS[event.category] || "bg-muted text-muted-foreground")} aria-hidden="true">{CATEGORY_ICONS[event.category] || "·"}</span>
      <span className="absolute bottom-0 left-[30px] top-12 w-px bg-neutral-100" aria-hidden="true" />
      <time className="text-[11px] leading-5 text-muted-foreground">{formatDate(event.occurred_at)}</time>
      <div className="grid min-w-0 gap-1.5">
        <h3 className="text-sm font-semibold leading-5 text-foreground">{event.title}</h3>
        <div className="flex flex-wrap items-center gap-1.5">
          {event.direction && <Badge>{event.direction}</Badge>}
          {event.status && <Badge status={statusTone(event.status)}>{String(event.status).replaceAll("_", " ")}</Badge>}
          <AttributionBadge actor={event.actor} />
        </div>
        {event.summary && <p className="line-clamp-3 whitespace-pre-line text-[13px] leading-6 text-muted-foreground">{event.summary}</p>}
        <div className="flex flex-wrap items-center gap-3">
          <RelatedEntity value={event.company} currentId={subjectId} onOpen={onOpenCompany} suffix="Company" />
          <RelatedEntity value={event.person} currentId={subjectId} onOpen={onOpenPerson} suffix="Person" />
          {event.source && <a className="text-[11px] font-medium text-primary underline decoration-primary/40 underline-offset-4 hover:text-primary-600" href={event.source.url} target="_blank" rel="noreferrer">{event.source.title || "Source"} ↗</a>}
        </div>
      </div>
    </article>
  );
}

export default function UnifiedTimeline({
  subjectType, subjectId, subjectName, people = [], refreshKey = 0, onOpenCompany, onOpenPerson, compact = false,
}) {
  const [filters, setFilters] = useState({ category: "all", direction: "all", person_id: "", date_from: "", date_to: "" });
  const [state, setState] = useState({ loading: true, events: [], total: 0, truncated: false, error: "" });
  const [visible, setVisible] = useState(12);
  const requestNumber = useRef(0);

  const load = useCallback(async () => {
    const currentRequest = ++requestNumber.current;
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await api.timeline({
        subject_type: subjectType,
        subject_id: subjectId,
        ...(filters.category !== "all" ? { category: filters.category } : {}),
        ...(filters.direction !== "all" ? { direction: filters.direction } : {}),
        ...(filters.person_id ? { person_id: filters.person_id } : {}),
        ...(filters.date_from ? { date_from: dateBoundary(filters.date_from) } : {}),
        ...(filters.date_to ? { date_to: dateBoundary(filters.date_to, true) } : {}),
        limit: 100,
      });
      if (currentRequest !== requestNumber.current) return;
      setState({ loading: false, events: data.events, total: data.total, truncated: data.truncated, error: "" });
      setVisible(12);
    } catch (error) {
      if (currentRequest !== requestNumber.current) return;
      setState((current) => ({ ...current, loading: false, error: error.message }));
    }
  }, [filters, subjectId, subjectType, refreshKey]);

  useEffect(() => { load(); }, [load]);
  function setFilter(field) {
    return (event) => setFilters((current) => ({ ...current, [field]: event.target.value }));
  }
  function clearFilters() {
    setFilters({ category: "all", direction: "all", person_id: "", date_from: "", date_to: "" });
  }
  const filtered = filters.category !== "all" || filters.direction !== "all" || filters.person_id || filters.date_from || filters.date_to;

  return (
    <section>
      <header className="flex min-h-14 flex-wrap items-center justify-between gap-3">
        <div className="grid gap-0.5">
          <h2 className="text-base font-semibold tracking-[-0.01em]">Unified activity</h2>
          <p className="text-xs text-muted-foreground">{state.total} event{state.total === 1 ? "" : "s"} connected to {subjectName}</p>
        </div>
        <Button variant="ghost" className="min-h-8 px-2.5 text-xs" disabled={state.loading} onClick={load}>{state.loading ? "Refreshing…" : "Refresh"}</Button>
      </header>

      <div className="mt-3 grid gap-4 rounded-xl bg-card p-4 shadow-soft">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter activity by type">
          {CATEGORY_OPTIONS.map(([value, label]) => (
            <FilterChip key={value} active={filters.category === value} onClick={() => setFilters((current) => ({ ...current, category: value }))}>{label}</FilterChip>
          ))}
        </div>
        <div className={cn(
          "grid gap-3",
          compact ? "sm:grid-cols-2" : (subjectType === "company" ? "sm:grid-cols-2 xl:grid-cols-4" : "sm:grid-cols-3"),
        )}>
          {subjectType === "company" && <SearchCombobox label="Person" hint="all if blank" value={filters.person_id} onChange={(person_id) => setFilters((current) => ({ ...current, person_id }))} options={people.map((person) => ({ value: person.id, label: person.name, description: person.role_title || person.title || person.business_email || "Linked person" }))} placeholder="Search linked people…" emptyMessage="No matching linked people" />}
          <SelectField label="Direction" value={filters.direction} onChange={setFilter("direction")}><option value="all">All directions</option><option value="outbound">Outbound</option><option value="inbound">Inbound</option><option value="internal">Internal</option></SelectField>
          <Field label="From" type="date" value={filters.date_from} max={filters.date_to || undefined} onChange={setFilter("date_from")} />
          <Field label="To" type="date" value={filters.date_to} min={filters.date_from || undefined} onChange={setFilter("date_to")} />
        </div>
        {filtered && (
          <div className="flex items-center justify-between gap-3 border-t border-neutral-100 pt-3">
            <p className="text-xs text-muted-foreground">Showing <b className="text-foreground">{state.events.length}</b> of {state.total} events</p>
            <Button variant="ghost" className="min-h-7 px-2.5 text-xs" onClick={clearFilters}>Clear filters</Button>
          </div>
        )}
      </div>

      {state.error && <div className="mt-4"><Notice>{state.error} <Button variant="ghost" className="ml-2 min-h-8 px-2" onClick={load}>Retry</Button></Notice></div>}
      {state.loading && !state.events.length ? <div className="mt-4"><LoadingRows count={5} /></div> : !state.events.length ? <EmptyState title="No matching activity" description="Memories, emails, calls, meetings, completed tasks, enrichment, and relationship changes connected to this record will appear here." /> : <>
        <div className="relative mt-2" role="log" aria-label={`${subjectName} activity timeline`}>{state.events.slice(0, visible).map((event) => <TimelineEvent compact={compact} key={event.id} event={event} subjectId={subjectId} onOpenCompany={onOpenCompany} onOpenPerson={onOpenPerson} />)}</div>
        {(visible < state.events.length || state.truncated) && <div className="flex justify-center py-5">{visible < state.events.length ? <Button onClick={() => setVisible((current) => current + 12)}>Show more activity</Button> : <p className="text-xs text-muted-foreground">Showing the latest {state.events.length} matching events. Use filters to narrow the timeline.</p>}</div>}
      </>}
    </section>
  );
}
